import UserAvatar16 from '@carbon/icons-react/es/user--avatar/16';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { TooltipPlacement } from 'antd/es/tooltip';
import assign from 'lodash/assign';
import differenceBy from 'lodash/differenceBy';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Team, User, UserType, PermissionSetType, Company } from 'api/models';
import { ProjectMembers } from 'api/project';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { HeightStyle } from 'components/atoms/input/input';
import { Loading } from 'components/atoms/loading';
import {
  PopoverMultiSelect,
  TriggerProps,
} from 'components/atoms/select/popover';
import { MultiValue, Option } from 'components/atoms/select/types';
import { Label01, Text02 } from 'components/styles';
import { MembersResponse } from 'data/members/api';
import { InvitedMembers } from 'data/members/slice';
import { useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';

const Loader = styled.div<{ size: SizeType }>`
  display: flex;
  align-items: center;
  min-height: ${HeightStyle};
  padding: 0 var(--spacing-05);
`;

const LoaderMessage = styled.span`
  ${Label01};
  ${Text02};
  margin-left: var(--spacing-04);
`;

export type SquadValue = {
  users: Pick<User, 'userId'>[];
  teams: Pick<Team, 'teamId'>[];
  newInvitees?: InvitedMembers[];
};

interface SquadPickerProps {
  className?: string;
  disabled?: boolean;
  onChange?: (value: SquadValue, hasNewInvitees: boolean) => void;
  onSelectionChange?: (value: SquadValue) => void;
  value?: SquadValue;
  size?: SizeType;
  options?: MembersResponse;
  isLoading?: boolean;
  renderTrigger?: (props: TriggerProps) => JSX.Element;
  placement?: TooltipPlacement;
  isMini?: boolean;
  allUsers?: User[];
}

export type Extra = { type: 'user' | 'team' | 'company'; id: number };

export const UserOption = (user: User, isInvited = true): Option<Extra> => {
  return {
    label: fullName(user),
    value: `user${user.userId}`,
    imageUrl: user.photoUrl,
    description: user.role?.roleName,
    logoUrl: user.company.companyLogoUrl,
    color: getRandomColor(user.userId),
    extra: { type: 'user', id: user.userId },
    invited: isInvited,
    userType: user.userType,
  };
};
export const CompanyOption = (company: Company): Option<Extra> => {
  return {
    label: company.companyName,
    value: `company${company.companyId}`,
    imageUrl: company.companyLogoUrl,
    color: getRandomColor(company.companyId),
    extra: { type: 'company', id: company.companyId },
  };
};

export const TeamOption = (team: Team): Option<Extra> => {
  return {
    label: team.teamName,
    value: `team${team.teamId}`,
    color: getRandomColor(team.teamId),
    extra: { type: 'team', id: team.teamId },
  };
};

const convertToValue = (
  value: SquadValue,
  squad: ProjectMembers,
): Option<Extra>[] => {
  const users = (value.users ?? [])
    .map((u) => (squad.users ?? []).find((user) => user.userId === u.userId))
    .filter(Boolean)
    .map((x) => UserOption(x!));

  const teams = (value.teams ?? [])
    .map((t) => (squad.teams ?? []).find((user) => user.teamId === t.teamId))
    .filter(Boolean)
    .map((t) => TeamOption(t!));

  return [...users, ...teams];
};

export const SquadPicker: FC<SquadPickerProps> = ({
  className,
  onChange,
  onSelectionChange,
  value = { users: [], teams: [] },
  size = 'middle',
  options = { users: [], teams: [], members: [] },
  isLoading = false,
  renderTrigger,
  placement,
  disabled = false,
  isMini = false,
  allUsers = [],
}) => {
  const [selectedValue, setSelectedValue] = useState<MultiValue>();
  const users = useTranslation('users');
  const teams = useTranslation('teams');
  const notInProject = useTranslation('not_in_project');

  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const [teamOptions, setTeamOptions] = useState<Option[]>([]);

  const canInviteusers = !isEmpty(allUsers);

  useDeepCompareEffect(() => {
    if (!isLoading) {
      if (value && !(isEmpty(options.teams) || isEmpty(options.users))) {
        const result = convertToValue(value, options);

        setSelectedValue(result);
      }
    }
  }, [value, options, isLoading]);

  const loadingMessage = useTranslation('loading');

  const trigger = ({
    value: selected,
    visible,
    openPopover,
    closePopover,
    ref,
  }: TriggerProps) => {
    const hasValue = selected && selected.length > 0;

    return (
      <Button
        className={className}
        type={hasValue ? 'text' : 'link'}
        size={size}
        onClick={() => (visible ? closePopover() : openPopover())}
        ref={ref}
        disabled={disabled}
        icon={!hasValue && <UserAvatar16 />}
      >
        {hasValue ? (
          <AvatarGroup maxCount={2}>
            {selected?.map((opt) => (
              <Avatar
                key={opt.value}
                name={opt.label}
                src={opt.imageUrl}
                companyLogoUrl={opt.logoUrl}
                size="small"
              />
            ))}
          </AvatarGroup>
        ) : (
          !isMini && <FormattedMessage id="choose" />
        )}
      </Button>
    );
  };

  const getNonMembersOptions = () =>
    differenceBy(allUsers, options.users ?? [], 'userId')?.map((u) =>
      UserOption(u, false),
    );
  const getSquadOptions = (): any => {
    if (options) {
      const defaultOptions = [
        {
          label: users,
          options: userOptions,
        },
        {
          label: teams,
          options: teamOptions,
        },
      ];

      if (canInviteusers) {
        return [
          ...defaultOptions,
          {
            label: notInProject,
            options: getNonMembersOptions(),
          },
        ];
      }

      return defaultOptions;
    }

    return false;
  };
  const SelectedOptionsToValue = (selected: MultiValue): SquadValue => {
    const grouped = groupBy(selected, 'extra.type');

    const groupedData = {
      users:
        grouped.user
          ?.map((u) => {
            const userId = u?.extra?.id;

            return { userId };
          })
          .filter(Boolean) ?? [],
      teams:
        grouped.team
          ?.map(({ extra }) => {
            const teamId = extra?.id;

            return { teamId };
          })
          .filter(Boolean) ?? [],
    };

    if (canInviteusers) {
      assign(groupedData, {
        newInvitees: grouped.user
          ?.filter((u) => !u.invited)
          ?.map((u) => ({
            user: { userId: u?.extra?.id },
            permissionSet: {
              permissionSetType: UserType.External
                ? PermissionSetType.CustomerCollaborator
                : PermissionSetType.Collaborator,
            },
          })),
      });
    }

    return groupedData;
  };

  return isLoading ? (
    <Loader size={size} title={loadingMessage}>
      <Loading size="small" />
      {!isMini && <LoaderMessage>{loadingMessage}</LoaderMessage>}
    </Loader>
  ) : (
    <PopoverMultiSelect
      placement={placement}
      value={selectedValue}
      renderTrigger={renderTrigger || trigger}
      triggerComponents={{
        TriggerPlaceholder: ({ isDisabled, onClick }) => (
          <Button
            type="link"
            size={size}
            disabled={isDisabled}
            onClick={() => {
              onClick();
            }}
            icon={<UserAvatar16 />}
          >
            <FormattedMessage id="choose" />
          </Button>
        ),
      }}
      options={getSquadOptions()}
      onChange={(latest) => {
        onSelectionChange?.(SelectedOptionsToValue(latest));
        setSelectedValue(latest);
      }}
      onClose={() => {
        if (onChange) {
          onChange(SelectedOptionsToValue(selectedValue), canInviteusers);
        }
      }}
      onOpen={(_selected) => {
        if (options) {
          let userOpts = orderBy(
            (options.users ?? []).map((u) => UserOption(u)),
            'label',
            ['desc'],
          );
          let teamOpts = orderBy(
            (options.teams ?? []).map(TeamOption),
            'label',
            ['desc'],
          );

          if (_selected) {
            const userSortArr = _selected
              .filter((r) => r.extra?.type === 'user')
              .map((r) => r.extra?.id);

            userOpts = userOpts
              .slice()
              .sort(
                (a, b) =>
                  userSortArr.indexOf(a.extra?.id) -
                  userSortArr.indexOf(b.extra?.id),
              )
              .reverse();
            const teamSortArr = _selected
              .filter((r) => r.extra?.type === 'team')
              .map((r) => r.extra?.id);

            teamOpts = teamOpts
              .slice()
              .sort(
                (a, b) =>
                  teamSortArr.indexOf(a.extra?.id) -
                  teamSortArr.indexOf(b.extra?.id),
              )
              .reverse();
          }

          setUserOptions(userOpts);
          setTeamOptions(teamOpts);
        }
      }}
    />
  );
};
